import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaBars, FaMagic, FaFilePdf, FaSpinner, FaPrint, FaEnvelope, FaCheck, FaTimes, FaFileWord, FaFileAlt } from "react-icons/fa";
import { TbFileTypeDocx } from "react-icons/tb";
import { saveAs } from "file-saver";
import { pdfExporter } from "quill-to-pdf";
import * as quillToWord from "quill-to-word";
import { v4 as uuidv4 } from "uuid";

function TopBar({ setIsSidebarOpen, isSidebarOpen, setShowPromptInput, quill }) {
  const [loadingDocx, setLoadingDocx] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [emailFileType, setEmailFileType] = useState("pdf");

  const generateFileName = (extension) => {
    const date = new Date().toISOString().split("T")[0];
    const uuid = uuidv4();
    return `intelleX_generated_${date}_${uuid}.${extension}`;
  };

  const generateDocx = async () => {
    if (!quill) return;
    const delta = quill.getContents();
    return await quillToWord.generateWord(delta, { exportAs: "blob" });
  };

  const generatePdf = async () => {
    if (!quill) return;
    const delta = quill.getContents();
    return await pdfExporter.generatePdf(delta);
  };

  const saveAsDocx = async () => {
    setLoadingDocx(true);
    try {
      const docxBlob = await generateDocx();
      const fileName = generateFileName("docx");
      saveAs(docxBlob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document. Please try again.");
    } finally {
      setLoadingDocx(false);
    }
  };

  const saveAsPdf = async () => {
    setLoadingPdf(true);
    try {
      const pdfBlob = await generatePdf();
      const fileName = generateFileName("pdf");
      saveAs(pdfBlob, fileName);
    } catch (error) {
      console.error("Error generating PDF document:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const printDocument = async () => {
    try {
      const pdfBlob = await generatePdf();
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl);
      printWindow.onload = () => {
        printWindow.print();
        URL.revokeObjectURL(pdfUrl);
      };
    } catch (error) {
      console.error("Error printing document:", error);
      alert("Failed to print document. Please try again.");
    }
  };

  const emailDocument = async () => {
    try {
      let blob;
      let fileName;
      if (emailFileType === 'pdf') {
        blob = await generatePdf();
        fileName = generateFileName("pdf");
      } else {
        blob = await generateDocx();
        fileName = generateFileName("docx");
      }
      await sendEmail(emailAddress, blob, fileName);
      alert(`Email with ${fileName} sent to ${emailAddress}`);
      setShowEmailModal(false);
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email. Please try again.");
    }
  };

  const sendEmail = async (to, blob, fileName) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`Sending ${fileName} to ${to}`);
        resolve();
      }, 2000);
    });
  };

  const confirmDialog = (action, message) => {
    setConfirmAction(() => action);
    setConfirmMessage(message);
    setShowConfirmDialog(true);
  };

  const CustomModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-4 rounded-lg shadow-xl max-w-sm w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FaTimes />
            </button>
          </div>
          {children}
        </div>
      </div>
    );
  };

  const ButtonIcon = ({ icon: Icon, onClick, bgColor, disabled = false }) => (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`p-2 ${bgColor} text-white rounded-full hover:opacity-80 transition-all duration-300 shadow-md`}
      disabled={disabled}
    >
      <Icon className="text-sm sm:text-base" />
    </motion.button>
  );

  return (
    <header className="bg-white shadow-sm z-10">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-2 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
          >
            <FaBars className="h-5 w-5" />
          </button>
          <h1 className="ml-2 text-lg sm:text-xl font-bold text-indigo-600 hidden sm:block">
            intelleX Docs
          </h1>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-2">
          <ButtonIcon
            icon={FaMagic}
            onClick={() => setShowPromptInput(true)}
            bgColor="bg-indigo-600"
          />
          <ButtonIcon
            icon={loadingDocx ? FaSpinner : TbFileTypeDocx}
            onClick={() => confirmDialog(saveAsDocx, "Are you sure you want to save as DOCX?")}
            bgColor="bg-emerald-500"
            disabled={loadingDocx}
          />
          <ButtonIcon
            icon={loadingPdf ? FaSpinner : FaFilePdf}
            onClick={() => confirmDialog(saveAsPdf, "Are you sure you want to save as PDF?")}
            bgColor="bg-rose-500"
            disabled={loadingPdf}
          />
          <ButtonIcon
            icon={FaPrint}
            onClick={() => confirmDialog(printDocument, "Are you sure you want to print the document?")}
            bgColor="bg-amber-500"
          />
          <ButtonIcon
            icon={FaEnvelope}
            onClick={() => setShowEmailModal(true)}
            bgColor="bg-purple-500"
          />
        </div>
      </div>

      <CustomModal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="Confirm Action"
      >
        <p className="mb-4 text-gray-600">{confirmMessage}</p>
        <div className="flex justify-end">
          <button
            onClick={() => setShowConfirmDialog(false)}
            className="mr-2 px-3 py-1 bg-gray-300 text-gray-700 rounded-full hover:bg-gray-400 transition-colors duration-300"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setShowConfirmDialog(false);
              confirmAction();
            }}
            className="px-3 py-1 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors duration-300 flex items-center"
          >
            <FaCheck className="mr-1" /> Confirm
          </button>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Email Document"
      >
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Recipient's Email
          </label>
          <input
            type="email"
            id="email"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            placeholder="Enter email address"
            className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            required
          />
        </div>
        <div className="mb-4 flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="pdf"
              checked={emailFileType === "pdf"}
              onChange={() => setEmailFileType("pdf")}
              className="mr-2"
            />
            <FaFileAlt className="mr-1" /> PDF
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="docx"
              checked={emailFileType === "docx"}
              onChange={() => setEmailFileType("docx")}
              className="mr-2"
            />
            <FaFileWord className="mr-1" /> DOCX
          </label>
        </div>
        <button
          onClick={emailDocument}
          className="w-full px-4 py-2 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors duration-300 flex items-center justify-center"
        >
          <FaEnvelope className="mr-2" /> Send Email
        </button>
      </CustomModal>
    </header>
  );
}

export default TopBar;